import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarClock, Video, Mic, BookOpen, ClipboardCheck, HeartHandshake, ShoppingBag, TrendingUp, ChevronRight, ExternalLink, Star, MessageSquareText, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { familiaApi, apiError, getUser } from "../lib/api";

const IMG = {
  sesiones_prox: "https://images.unsplash.com/photo-1616543791139-fdca422b7ef7?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  sesiones_grab: "https://images.unsplash.com/photo-1513759565286-20e9c5fad06b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  podcast: "https://images.unsplash.com/photo-1589903308904-1010c2294adc?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  libro: "https://images.unsplash.com/photo-1506880018603-83d5b814b5a6?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  evaluaciones: "https://images.unsplash.com/photo-1600880292089-90a7e086ee0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  desarrollo: "https://images.unsplash.com/photo-1529180979161-06b8b6d6f2be?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  market: "https://images.unsplash.com/photo-1583321500900-82807e458f3c?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  investors: "https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  connect: "https://images.unsplash.com/photo-1478737270239-2f02b77fc618?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
};

const CARDS = [
  { key: "sesiones_prox", title: "Próximas sesiones", desc: "Clases y encuentros para padres y madres.", icon: CalendarClock },
  { key: "sesiones_grab", title: "Sesiones grabadas", desc: "Revive las sesiones cuando quieras.", icon: Video },
  { key: "podcast", title: "Podcast Padres y Madres", desc: "Escucha episodios inspiradores.", icon: Mic },
  { key: "libro", title: "Libro del mes", desc: "La lectura del mes para tu familia.", icon: BookOpen },
  { key: "evaluaciones", title: "Evaluaciones", desc: "Evalúa a Supera Leadership y al instructor.", icon: ClipboardCheck },
  { key: "desarrollo", title: "Desarrollo de mi Hijo/a", desc: "Retroalimentación y diálogos mensuales.", icon: HeartHandshake },
  { key: "market", title: "Supera Market", desc: "Beneficios y productos Supera.", icon: ShoppingBag },
  { key: "investors", title: "Supera Investors", desc: "Oportunidades de inversión Supera.", icon: TrendingUp },
  { key: "connect", title: "Supera Connect", desc: "Entra a tus clases en vivo.", icon: Video },
];

export default function PadresPortal() {
  const navigate = useNavigate();
  const [view, setView] = useState(null);
  const user = getUser();

  const open = (key) => {
    if (key === "connect") { navigate("/connect"); return; }
    setView(key);
  };

  return (
    <div className="min-h-screen bg-[#F4F6F9]">
      <div className="bg-gradient-to-r from-[#0A1C30] to-[#173a5e] text-white">
        <div className="max-w-5xl mx-auto px-5 py-6">
          <button data-testid="padres-back" onClick={() => view ? setView(null) : navigate("/mi-cuenta")} className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm font-bold"><ArrowLeft className="w-4 h-4" /> {view ? "Volver a Padres y Madres" : "Volver al inicio"}</button>
          <div className="flex items-center gap-3 mt-3">
            <HeartHandshake className="w-8 h-8 text-[var(--gold)]" />
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold">Padres y Madres Supera</h1>
              <p className="text-white/70 text-sm">Acompaña de cerca el desarrollo y liderazgo de tu hijo/a.</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-5 py-8">
        {!view ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="padres-grid">
            {CARDS.map((c) => (
              <button key={c.key} data-testid={`padres-card-${c.key}`} onClick={() => open(c.key)} className="group text-left rounded-3xl overflow-hidden bg-white ring-1 ring-black/5 hover:shadow-xl transition-all">
                <div className="relative h-36 overflow-hidden">
                  <img src={IMG[c.key]} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A1C30]/80 to-transparent" />
                  <span className="absolute bottom-3 left-3 w-11 h-11 rounded-2xl bg-[var(--gold)] flex items-center justify-center"><c.icon className="w-6 h-6 text-[#0A1C30]" /></span>
                </div>
                <div className="p-4">
                  <p className="font-extrabold text-[#0A1C30]">{c.title}</p>
                  <p className="text-sm text-[#6B7688] mt-0.5">{c.desc}</p>
                </div>
              </button>
            ))}
          </div>
        ) : view === "evaluaciones" ? (
          <Evaluaciones />
        ) : view === "desarrollo" ? (
          <Desarrollo />
        ) : (
          <ContenidoSeccion seccion={view} card={CARDS.find((c) => c.key === view)} />
        )}
      </div>
    </div>
  );
}

function SectionTitle({ card }) {
  return (
    <div className="flex items-center gap-3 mb-5">
      <span className="w-11 h-11 rounded-2xl bg-[#0A1C30] flex items-center justify-center"><card.icon className="w-6 h-6 text-[var(--gold)]" /></span>
      <h2 className="text-xl font-extrabold text-[#0A1C30]">{card.title}</h2>
    </div>
  );
}

function ContenidoSeccion({ seccion, card }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    familiaApi.contenido(seccion).then((d) => setItems(d.items)).catch((e) => { toast.error(apiError(e)); setItems([]); });
  }, [seccion]);
  return (
    <div>
      <SectionTitle card={card} />
      {items === null ? (
        <div className="flex items-center gap-2 text-[#6B7688]"><Loader2 className="w-5 h-5 animate-spin" /> Cargando…</div>
      ) : items.length === 0 ? (
        <div className="bg-white rounded-3xl ring-1 ring-black/5 p-12 text-center"><Sparkles className="w-10 h-10 text-[#C7CDD6] mx-auto" /><p className="mt-3 text-[#6B7688] font-semibold">Aún no hay contenido disponible en esta sección.</p></div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4" data-testid="padres-contenido-list">
          {items.map((it) => (
            <div key={it.id} className="bg-white rounded-2xl ring-1 ring-black/5 overflow-hidden">
              {it.imagen_url && <img src={it.imagen_url} alt={it.titulo} className="w-full h-40 object-cover" />}
              <div className="p-4">
                <p className="font-extrabold text-[#0A1C30]">{it.titulo}</p>
                {(it.fecha || it.hora) && <p className="text-xs font-bold text-[var(--gold)] mt-0.5">{it.fecha}{it.hora ? ` · ${it.hora}` : ""}</p>}
                {it.descripcion && <p className="text-sm text-[#6B7688] mt-1 whitespace-pre-wrap">{it.descripcion}</p>}
                {it.url && <a href={it.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 rounded-xl bg-[#0A1C30] text-white font-bold px-4 py-2 text-sm hover:bg-[#173a5e]"><ExternalLink className="w-4 h-4" /> Abrir</a>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Evaluaciones() {
  const [tipo, setTipo] = useState("leadership");
  const [preguntas, setPreguntas] = useState(null);
  const [resp, setResp] = useState({});
  const [comentario, setComentario] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setPreguntas(null); setResp({}); setComentario("");
    familiaApi.evalPreguntas(tipo).then((d) => setPreguntas(d.preguntas)).catch((e) => { toast.error(apiError(e)); setPreguntas([]); });
  }, [tipo]);
  const submit = async () => {
    if (!preguntas || preguntas.length === 0) return;
    if (preguntas.some((p) => !resp[p.id])) { toast.error("Responde todas las preguntas."); return; }
    setSaving(true);
    try { await familiaApi.evalSubmit({ tipo, respuestas: resp, comentario }); toast.success("¡Gracias! Tu evaluación fue enviada."); setResp({}); setComentario(""); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <div>
      <SectionTitle card={{ title: "Evaluaciones", icon: ClipboardCheck }} />
      <div className="flex gap-2 mb-5">
        <button data-testid="eval-tab-leadership" onClick={() => setTipo("leadership")} className={`rounded-xl px-4 py-2 text-sm font-bold ${tipo === "leadership" ? "bg-[#0A1C30] text-white" : "bg-white ring-1 ring-black/5 text-[#6B7688]"}`}>Evaluar Supera Leadership</button>
        <button data-testid="eval-tab-instructor" onClick={() => setTipo("instructor")} className={`rounded-xl px-4 py-2 text-sm font-bold ${tipo === "instructor" ? "bg-[#0A1C30] text-white" : "bg-white ring-1 ring-black/5 text-[#6B7688]"}`}>Evaluar Instructor</button>
      </div>
      {preguntas === null ? (
        <div className="flex items-center gap-2 text-[#6B7688]"><Loader2 className="w-5 h-5 animate-spin" /> Cargando…</div>
      ) : preguntas.length === 0 ? (
        <div className="bg-white rounded-3xl ring-1 ring-black/5 p-10 text-center text-[#6B7688] font-semibold">Aún no hay preguntas configuradas para esta evaluación.</div>
      ) : (
        <div className="bg-white rounded-3xl ring-1 ring-black/5 p-6" data-testid="eval-form">
          <div className="space-y-5">
            {preguntas.map((p, i) => (
              <div key={p.id}>
                <p className="font-bold text-[#0A1C30] text-sm">{i + 1}. {p.texto}</p>
                <div className="flex gap-1.5 mt-2">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} data-testid={`eval-${p.id}-${n}`} onClick={() => setResp((r) => ({ ...r, [p.id]: n }))} className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${(resp[p.id] || 0) >= n ? "bg-[var(--gold)]" : "bg-[#F4F6F9]"}`}>
                      <Star className={`w-5 h-5 ${(resp[p.id] || 0) >= n ? "text-white fill-white" : "text-[#C7CDD6]"}`} />
                    </button>
                  ))}
                </div>
              </div>
            ))}
            <div>
              <p className="font-bold text-[#0A1C30] text-sm mb-1">Comentario (opcional)</p>
              <textarea value={comentario} onChange={(e) => setComentario(e.target.value)} className="w-full min-h-[90px] rounded-xl border border-black/10 p-3 text-sm outline-none focus:ring-2 focus:ring-[var(--gold)]" placeholder="Comparte tus comentarios…" />
            </div>
            <button data-testid="eval-submit" onClick={submit} disabled={saving} className="rounded-xl bg-[var(--gold)] text-[#0A1C30] font-extrabold px-6 py-2.5 disabled:opacity-60">{saving ? "Enviando…" : "Enviar evaluación"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Desarrollo() {
  const [hijos, setHijos] = useState(null);
  const [sel, setSel] = useState(null);
  const [data, setData] = useState(null);
  useEffect(() => {
    familiaApi.misHijos().then((d) => { setHijos(d.hijos); if (d.hijos.length === 1) setSel(d.hijos[0].id); }).catch((e) => { toast.error(apiError(e)); setHijos([]); });
  }, []);
  useEffect(() => {
    if (!sel) { setData(null); return; }
    setData(undefined);
    familiaApi.desarrollo(sel).then((d) => setData(d)).catch((e) => { toast.error(apiError(e)); setData(null); });
  }, [sel]);
  return (
    <div>
      <SectionTitle card={{ title: "Desarrollo de mi Hijo/a", icon: HeartHandshake }} />
      {hijos === null ? (
        <div className="flex items-center gap-2 text-[#6B7688]"><Loader2 className="w-5 h-5 animate-spin" /> Cargando…</div>
      ) : hijos.length === 0 ? (
        <div className="bg-white rounded-3xl ring-1 ring-black/5 p-10 text-center text-[#6B7688] font-semibold">No tienes candidatos/as vinculados.</div>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mb-5">
            {hijos.map((h) => (
              <button key={h.id} data-testid={`hijo-tab-${h.id}`} onClick={() => setSel(h.id)} className={`rounded-xl px-4 py-2 text-sm font-bold ${sel === h.id ? "bg-[#7C4DDB] text-white" : "bg-white ring-1 ring-black/5 text-[#6B7688]"}`}>{h.nombre}</button>
            ))}
          </div>
          {!sel ? <p className="text-[#6B7688] text-sm">Selecciona a tu hijo/a para ver su desarrollo.</p>
            : data === undefined ? <div className="flex items-center gap-2 text-[#6B7688]"><Loader2 className="w-5 h-5 animate-spin" /> Cargando…</div>
            : data ? (
              <div className="space-y-6" data-testid="desarrollo-view">
                <div className="bg-white rounded-2xl ring-1 ring-black/5 p-5">
                  <p className="text-sm font-extrabold text-[#0A1C30] flex items-center gap-1.5 mb-3"><MessageSquareText className="w-4 h-4" /> Retroalimentación del staff</p>
                  {(data.desarrollo.retroalimentaciones || []).length === 0 ? <p className="text-sm text-[#9AA3B2]">Aún no hay retroalimentación.</p> :
                    <div className="space-y-2">{data.desarrollo.retroalimentaciones.map((r) => (
                      <div key={r.id} className="rounded-xl bg-[#F4F6F9] p-3"><p className="text-sm text-[#2A3446] whitespace-pre-wrap">{r.texto}</p><p className="text-[11px] text-[#9AA3B2] mt-1">{r.autor} · {(r.fecha || "").slice(0, 10)}</p></div>
                    ))}</div>}
                </div>
                <div className="bg-white rounded-2xl ring-1 ring-black/5 p-5">
                  <p className="text-sm font-extrabold text-[#0A1C30] flex items-center gap-1.5 mb-3"><Video className="w-4 h-4" /> Grabaciones Diálogo Mensual</p>
                  {(data.desarrollo.dialogos || []).length === 0 ? <p className="text-sm text-[#9AA3B2]">Aún no hay diálogos registrados.</p> :
                    <div className="space-y-3">{data.desarrollo.dialogos.map((d) => (
                      <div key={d.id} className="rounded-xl ring-1 ring-black/5 p-3">
                        <p className="text-sm font-bold text-[#0A1C30]">{d.titulo} <span className="text-xs font-normal text-[#6B7688]">· {d.fecha}</span></p>
                        {d.grabacion_url && <a href={d.grabacion_url} target="_blank" rel="noreferrer" className="text-xs font-bold text-[#7C4DDB] hover:underline break-all inline-flex items-center gap-1 mt-1"><ExternalLink className="w-3.5 h-3.5" /> Ver grabación</a>}
                        {d.acuerdos && <div className="mt-2"><p className="text-[11px] font-bold text-[#9AA3B2] uppercase">Acuerdos y compromisos</p><p className="text-sm text-[#2A3446] whitespace-pre-wrap">{d.acuerdos}</p></div>}
                      </div>
                    ))}</div>}
                </div>
              </div>
            ) : null}
        </>
      )}
    </div>
  );
}
